/**
 * Automatic Sync Hook
 * 
 * Provides automatic synchronization without requiring manual component-level integration.
 * Simply use this hook in GameCanvas and all caching, optimistic updates, and entity sync
 * happen automatically.
 */

import { useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import AutomaticSyncIntegration from '../utils/AutomaticSyncIntegration';

export interface UseAutomaticSyncConfig {
  socket: Socket | null;
  enabled?: boolean;
}

export const useAutomaticSync = (config: UseAutomaticSyncConfig) => {
  const initRef = useRef(false);
  const integrationRef = useRef<AutomaticSyncIntegration | null>(null);

  // Initialize automatic sync on socket connection
  useEffect(() => {
    if (initRef.current || !config.socket || config.enabled === false) return;
    initRef.current = true;

    integrationRef.current = AutomaticSyncIntegration.getInstance();
    integrationRef.current.initialize({
      socket: config.socket,
      enableAutoCache: true,
      enableAutoOptimistic: true,
      enableAutoEntitySync: true,
      enableAutoConflictResolution: true,
    });

    console.log('✅ Automatic sync initialized');

    return () => {
      integrationRef.current?.disconnect();
      initRef.current = false;
    };
  }, [config.socket, config.enabled]);

  return {
    integration: integrationRef.current,
  };
};

export default useAutomaticSync;
